import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import {
  Phone,
  MapPin,
  Clock,
  Check,
  Shield,
  ArrowRight,
  Star,
  KeyRound,
  DoorOpen,
  ShieldAlert,
  Mailbox,
  BadgeCheck,
  ChevronRight,
  Home as HomeIcon,
} from "lucide-react";
import { COMPANY, CITIES, SERVICES, PRO_AUDIENCES } from "../lib/constants";
import useSeo from "../lib/useSeo";
import { citySeo, serviceCityPath } from "../lib/seoPages";
import { SERVICE_PAGES } from "../lib/servicePages";
import BrandMarquee from "../components/BrandMarquee";
import TruckCard from "../components/TruckCard";
import PricingGrid from "../components/PricingGrid";
import ArtisanSection from "../components/ArtisanSection";

const ICON_MAP = { KeyRound, DoorOpen, ShieldAlert, Shield, Mailbox, BadgeCheck };

export default function CityPage({ citySlug }) {
  const params = useParams();
  const slug = citySlug || params.slug;
  const city = CITIES.find((c) => c.slug === slug);

  const seo = city ? citySeo(city) : { title: "Serrurier Bretagne — Mon Serrurier Breton", description: "Serrurier local breton, dépannage 24h/24.", path: "/zones-intervention", faq: [], jsonLd: [] };
  const localFaq = seo.faq;

  useSeo({ title: seo.title, description: seo.description, path: seo.path, jsonLd: seo.jsonLd });

  if (!city) return <Navigate to="/zones-intervention" replace />;

  const neighbors = (city.neighbors || [])
    .map((s) => CITIES.find((c) => c.slug === s))
    .filter(Boolean);

  return (
    <div data-testid={`page-city-${city.slug}`}>
      {/* Breadcrumb */}
      <nav
        data-testid="city-breadcrumb"
        className="max-w-7xl mx-auto px-4 md:px-8 pt-8 text-xs text-slate-500 flex items-center gap-2 flex-wrap"
        aria-label="Fil d'Ariane"
      >
        <Link to="/" className="inline-flex items-center gap-1 hover:text-teal-700">
          <HomeIcon className="w-3.5 h-3.5" /> Accueil
        </Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <Link to="/zones-intervention" className="hover:text-teal-700">
          Zones d'intervention
        </Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="text-slate-900 font-semibold">{city.name}</span>
      </nav>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pt-10 md:pt-14 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 space-y-6">
            <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-widest">
              <MapPin className="w-3.5 h-3.5" />
              {city.departement} ({city.dept}) · {city.postal}
              {city.base && (
                <span className="ml-2 px-2 py-0.5 rounded-full bg-teal-600 text-white text-[10px]">
                  Notre base
                </span>
              )}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-slate-900 leading-[0.95]">
              Serrurier à <span className="text-teal-600">{city.name}</span>
              <br />— Intervention en {seo.time}, 24h/24.
            </h1>
            <p className="text-base md:text-lg text-slate-600 max-w-2xl leading-relaxed">
              {city.lead}
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                data-testid="city-cta-call"
                href={`tel:${COMPANY.phoneDigits}`}
                className="inline-flex items-center gap-3 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-full px-7 py-4 shadow-xl shadow-orange-600/30 text-lg pulse-emergency"
              >
                <Phone className="w-5 h-5" />
                {COMPANY.phone}
              </a>
              <Link
                data-testid="city-cta-contact"
                to="/contact"
                className="inline-flex items-center gap-2 bg-white border-2 border-slate-900 text-slate-900 font-bold rounded-full px-6 py-4 hover:bg-slate-900 hover:text-white transition-all"
              >
                Devis gratuit <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 pt-4 border-t border-slate-200">
              <div className="flex items-center gap-1.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                ))}
                <span className="ml-1 font-bold text-slate-900">{COMPANY.googleRating}/5</span>
                <span className="text-xs text-slate-500">{COMPANY.googleReviews} avis Google</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Check className="w-4 h-4 text-teal-600" /> Artisan diplômé
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Check className="w-4 h-4 text-teal-600" /> Devis gratuit
              </div>
            </div>
          </div>

          {/* Stats card */}
          <div className="lg:col-span-5">
            <div className="rounded-3xl bg-slate-900 text-white p-8 relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-teal-500/20 blur-3xl" />
              <div className="relative">
                <div className="text-xs font-bold uppercase tracking-widest text-teal-300 mb-1">
                  Intervention à {city.name}
                </div>
                <div className="font-display text-4xl md:text-5xl font-extrabold">
                  {city.distance === 0 ? "Immédiat" : `~${city.distance} km`}
                </div>
                <div className="text-slate-300 mt-1 text-sm">
                  depuis notre base de Mont-Dol
                </div>

                <div className="mt-6 grid grid-cols-3 gap-3">
                  <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
                    <Clock className="w-5 h-5 text-teal-300 mb-2" />
                    <div className="font-display text-xl font-extrabold">{seo.time}</div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">
                      Arrivée moyenne
                    </div>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
                    <Shield className="w-5 h-5 text-teal-300 mb-2" />
                    <div className="font-display text-xl font-extrabold">A2P</div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">
                      Matériel certifié
                    </div>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
                    <BadgeCheck className="w-5 h-5 text-teal-300 mb-2" />
                    <div className="font-display text-xl font-extrabold">24/7</div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">
                      Disponibilité
                    </div>
                  </div>
                </div>

                <a
                  href={`tel:${COMPANY.phoneDigits}`}
                  className="mt-6 inline-flex items-center gap-2 text-amber-400 font-bold hover:text-amber-300"
                >
                  Appeler pour {city.name} <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Votre artisan local */}
      <ArtisanSection />

      {/* Services in city */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-20">
        <div className="max-w-2xl mb-10">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-teal-700 mb-3">
            <span className="h-px w-8 bg-teal-700" /> Nos services à {city.name}
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
            Serrurier <span className="text-teal-600">{city.name}</span> :
            toutes nos interventions.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map((s, i) => {
            const Icon = ICON_MAP[s.icon] || Shield;
            const page = SERVICE_PAGES.find((p) => p.serviceId === s.id);
            const Wrapper = page ? Link : "div";
            return (
              <Wrapper
                key={s.id}
                {...(page ? { to: serviceCityPath(page, city) } : {})}
                data-testid={`city-service-${s.id}`}
                className="group block bg-white border border-slate-200 rounded-3xl p-7 hover:border-teal-500 hover:shadow-lg transition-all"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-2xl bg-teal-50 text-teal-700 flex items-center justify-center group-hover:bg-teal-600 group-hover:text-white transition-colors">
                    <Icon className="w-6 h-6" />
                  </div>
                  <span className="text-slate-300 font-display font-black text-xl">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="font-display text-lg font-bold text-slate-900 mb-2">
                  {s.title} à {city.name}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {s.short}
                </p>
                {page && (
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-teal-700">
                    Voir {s.title.toLowerCase()} à {city.name} <ArrowRight className="w-4 h-4" />
                  </span>
                )}
              </Wrapper>
            );
          })}
        </div>
      </section>

      {/* Truck mobile section */}
      <TruckCard cityName={city.name} />

      {/* Pricing grid */}
      <PricingGrid cityName={city.name} />


      {/* Why local trust block */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 bg-white border border-slate-200 rounded-3xl p-8 md:p-12">
          <div className="lg:col-span-7 space-y-4">
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-teal-700">
              <span className="h-px w-8 bg-teal-700" /> Artisan local
            </div>
            <h2 className="font-display text-2xl md:text-4xl font-extrabold tracking-tight text-slate-900 leading-tight">
              Pourquoi choisir un serrurier local à {city.name} ?
            </h2>
            <ul className="space-y-3 pt-2">
              {[
                `Intervention rapide sur ${city.name} et alentours (${city.distance === 0 ? "< 10 min" : `environ ${city.distance} km de notre atelier`})`,
                "Tarifs annoncés avant toute intervention, devis gratuit",
                "Artisan breton diplômé, pas d'intermédiaire ni de sous-traitance",
                "Matériel professionnel (Picard, Vachette, Bricard, Assa Abloy, Héraclès)",
                "Facture détaillée pour remboursement assurance",
                "Disponible 24h/24, 7j/7 — week-ends et jours fériés inclus",
              ].map((it) => (
                <li key={it} className="flex items-start gap-3 text-slate-700">
                  <span className="mt-0.5 w-5 h-5 rounded-full bg-teal-600 text-white flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3" />
                  </span>
                  <span className="leading-relaxed">{it}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-2xl bg-teal-50 border border-teal-100 p-6">
              <div className="text-xs font-bold uppercase tracking-widest text-teal-700 mb-2">
                En urgence à {city.name} ?
              </div>
              <div className="font-display text-2xl font-extrabold text-slate-900 mb-2">
                On décroche, on arrive.
              </div>
              <p className="text-sm text-slate-700 leading-relaxed">
                Porte claquée, clé perdue ou cassée, effraction : un appel
                suffit. Nous sommes joignables à toute heure.
              </p>
              <a
                data-testid="city-urgency-call"
                href={`tel:${COMPANY.phoneDigits}`}
                className="mt-5 inline-flex items-center gap-2 bg-orange-600 text-white font-bold rounded-full px-5 py-3 shadow-lg shadow-orange-600/30"
              >
                <Phone className="w-4 h-4" /> {COMPANY.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      <BrandMarquee />


      {/* P2 — Encart Pro pour gestionnaires */}
      <section
        data-testid="city-pro-banner"
        className="max-w-7xl mx-auto px-4 md:px-8 py-12"
      >
        <div className="rounded-3xl bg-gradient-to-br from-teal-50 via-white to-slate-50 border border-teal-200 p-8 md:p-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
            <div className="lg:col-span-7 space-y-3">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-teal-700">
                <BadgeCheck className="w-4 h-4" /> Vous êtes professionnel à {city.name} ?
              </div>
              <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900 leading-tight">
                Hôteliers · Syndics · Agences · Conciergeries
              </h2>
              <p className="text-slate-700 leading-relaxed">
                Nous gérons aussi les besoins serrurerie des{" "}
                <strong>hôtels, copropriétés, agences immobilières</strong> et{" "}
                <strong>conciergeries de {city.name}</strong> : référent
                unique, devis cadre, astreinte 24/7, facturation pro à 30
                jours, audits annuels.
              </p>
            </div>
            <div className="lg:col-span-5 flex flex-col gap-2">
              {PRO_AUDIENCES.map((a) => (
                <Link
                  key={a.slug}
                  to={`/serrurier-${a.slug}`}
                  data-testid={`city-pro-link-${a.slug}`}
                  className="group bg-white border border-slate-200 rounded-2xl px-4 py-3 hover:border-teal-500 hover:shadow-md transition-all flex items-center justify-between gap-3"
                >
                  <span>
                    <span className="font-display font-bold text-slate-900 group-hover:text-teal-700 capitalize">
                      Serrurier {a.keyword}
                    </span>
                    <span className="block text-xs text-slate-500">
                      Page dédiée — Saint-Malo, Dinard & alentours
                    </span>
                  </span>
                  <ArrowRight className="w-4 h-4 text-teal-600 group-hover:translate-x-1 transition-transform flex-shrink-0" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>


      {/* Local FAQ — SEO content */}
      <section
        data-testid="city-local-faq"
        className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-20"
      >
        <div className="max-w-2xl mb-10">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-teal-700 mb-3">
            <span className="h-px w-8 bg-teal-700" /> FAQ — Serrurier {city.name}
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
            Questions fréquentes
            <br />
            <span className="text-teal-600">à {city.name}.</span>
          </h2>
        </div>
        <div className="space-y-3">
          {localFaq.map((it, i) => (
            <details
              key={i}
              data-testid={`city-faq-${i + 1}`}
              className="group rounded-3xl border border-slate-200 bg-white p-6 md:p-7 open:border-teal-500 open:shadow-lg transition-all"
              open={i === 0}
            >
              <summary className="flex items-start justify-between gap-4 cursor-pointer list-none">
                <span className="flex items-start gap-4">
                  <span className="font-display font-black text-slate-300 group-open:text-teal-600">
                    0{i + 1}
                  </span>
                  <span className="font-display text-lg md:text-xl font-bold text-slate-900">
                    {it.q}
                  </span>
                </span>
                <ChevronRight className="w-5 h-5 mt-1 flex-shrink-0 text-slate-500 group-open:rotate-90 group-open:text-teal-600 transition-transform" />
              </summary>
              <div className="pl-10 mt-3 text-slate-700 leading-relaxed">
                {it.a}
              </div>
            </details>
          ))}
        </div>

        {/* SEO copy — long-tail keywords */}
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-slate-50 border border-slate-200 rounded-3xl p-7 md:p-8">
            <h3 className="font-display text-xl font-extrabold text-slate-900 mb-3">
              Serrurier {city.name} : interventions courantes
            </h3>
            <p className="text-slate-700 leading-relaxed text-sm md:text-base">
              À {city.name} ({city.postal}), nos interventions les plus
              demandées sont l'<strong>ouverture de porte claquée</strong>, le{" "}
              <strong>changement de cylindre</strong> après perte de clés, le{" "}
              <strong>remplacement de serrure</strong> standard ou multipoints,
              et la <strong>mise en sécurité après tentative d'effraction</strong>.
              Nous intervenons aussi pour l'installation de blindages, cylindres
              haute sécurité (Picard, Vachette, Bricard, Héraclès, Assa Abloy)
              et serrures de boîtes aux lettres.
            </p>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-3xl p-7 md:p-8">
            <h3 className="font-display text-xl font-extrabold text-slate-900 mb-3">
              Pourquoi un artisan local à {city.name} ?
            </h3>
            <p className="text-slate-700 leading-relaxed text-sm md:text-base">
              Faire appel à un <strong>serrurier {city.name}</strong> local
              comme Mon Serrurier Breton, c'est éviter les réseaux nationaux
              opaques aux tarifs gonflés. Vous échangez directement avec votre
              artisan, le tarif est annoncé au téléphone, l'intervention est
              soignée et la facture est acceptée par votre assurance habitation.
              {city.distance > 0
                ? ` ${city.name} étant à environ ${city.distance} km de notre atelier, comptez ${seo.time} pour notre arrivée.`
                : ` ${city.name} étant notre commune d'implantation, l'intervention est quasi immédiate.`}
            </p>
          </div>
        </div>

        {city.localSeo && (
          <div className="mt-6 bg-white border border-slate-200 rounded-3xl p-7 md:p-9">
            <h3 className="font-display text-xl md:text-2xl font-extrabold text-slate-900 mb-4">
              {city.name} — bien connaître votre commune pour mieux intervenir
            </h3>
            <div className="text-slate-700 leading-relaxed text-sm md:text-base space-y-3">
              {city.localSeo.split("\n\n").map((p, i) => (
                <p key={i} dangerouslySetInnerHTML={{ __html: p }} />
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Charte trust banner */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        <div
          data-testid="city-charte-banner"
          className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-900 to-teal-900 text-white p-8 md:p-12"
        >
          <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-amber-300/15 blur-3xl" />
          <div className="absolute -bottom-16 -left-16 w-72 h-72 rounded-full bg-teal-500/20 blur-3xl" />
          <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-2 flex md:justify-center">
              <div className="w-20 h-20 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center">
                <Shield className="w-10 h-10 text-amber-300" />
              </div>
            </div>
            <div className="md:col-span-7 space-y-3">
              <div className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-400/40 text-orange-200 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest">
                Charte de transparence
              </div>
              <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight leading-tight">
                À {city.name} comme partout :
                <br />
                <span className="text-amber-300">aucune arnaque, aucune surprise.</span>
              </h2>
              <p className="text-teal-50/90 text-sm md:text-base leading-relaxed max-w-xl">
                Tarifs publiés, devis confirmé avant intervention, ouverture
                fine en priorité, facture détaillée. Découvrez nos{" "}
                <strong className="text-white">10 engagements concrets</strong> contre les
                arnaques de serrurerie.
              </p>
            </div>
            <div className="md:col-span-3 flex md:justify-end">
              <Link
                to="/charte-transparence"
                data-testid="city-charte-link"
                className="inline-flex items-center gap-2 bg-amber-300 hover:bg-amber-200 text-slate-900 font-bold rounded-full px-6 py-4 shadow-xl whitespace-nowrap"
              >
                Voir la charte <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Nearby cities */}
      {neighbors.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-20">
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-teal-700 mb-2">
              <MapPin className="w-4 h-4" /> Autour de {city.name}
            </div>
            <h2 className="font-display text-2xl md:text-4xl font-extrabold tracking-tight text-slate-900">
              Serrurier dans les communes voisines
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {neighbors.map((n) => (
              <Link
                key={n.slug}
                to={`/serrurier-${n.slug}`}
                data-testid={`neighbor-link-${n.slug}`}
                className="group flex items-center justify-between gap-3 bg-white border border-slate-200 hover:border-teal-500 hover:shadow-md transition-all rounded-2xl p-4"
              >
                <div>
                  <div className="font-display font-bold text-slate-900 group-hover:text-teal-700">
                    Serrurier {n.name}
                  </div>
                  <div className="text-xs text-slate-500">
                    {n.postal} · {n.dept}
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-teal-600 group-hover:translate-x-1 transition-transform" />
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-20">
        <div className="rounded-3xl bg-gradient-to-br from-teal-700 via-teal-800 to-slate-900 p-10 md:p-14 text-white relative overflow-hidden">
          <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-amber-400/20 blur-3xl" />
          <div className="relative grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
                Besoin d'un serrurier à {city.name} ?
              </h2>
              <p className="text-teal-100 mt-3 max-w-md">
                Intervention en {seo.time}, devis gratuit, tarifs transparents.
              </p>
            </div>
            <div className="flex md:justify-end flex-wrap gap-3">
              <a
                data-testid="city-final-call"
                href={`tel:${COMPANY.phoneDigits}`}
                className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full px-7 py-4 shadow-xl text-lg"
              >
                <Phone className="w-5 h-5" />
                {COMPANY.phone}
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-white text-slate-900 font-bold rounded-full px-6 py-4"
              >
                Formulaire <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
